import { Text, TextType } from '@bit/first-scope.text';
import { useTheme } from '@cthings.co/styled-components';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectIsAccess, selectLanguageStrings, selectUserAccessManagement } from '../../../../../../../app/state/user';
import { API_URL } from '../../../../../../../consts';
import { useTableFunctions, withTableContextProvider } from '../../../../../../../features/universalTable/context';
import {
  InlineActionType,
  RowItemType,
  TableCustomComponentState,
} from '../../../../../../../features/universalTable/types';
import { UniversalTable } from '../../../../../../../features/universalTable/UniversalTable';
import { PATHS } from '../../../../../../../routes/paths';
import { View } from '../../../../../../../routes/routeInterfaces';
import { useMediaType } from '@cthings.co/styles-utils';
import { useHistory } from '../../../../../../../utils/react-router-dom-abstraction';
import { useQueryParams } from '../../../../../../../utils/useQueryParams';
import { EventField } from './components/eventField/EventField';

const TableControl = ({ pathParams, isInDevices }: { pathParams: any; isInDevices?: boolean }) => {
  const keyword = 'EventsTable';
  const offsetKey = 'offsetEvents';

  const sortingKeys = { sort_type: 'sort_type', sort_field: 'sort_field' };
  const queryParams = useQueryParams() || {};
  const offset = queryParams[offsetKey] || '0';
  const sortingData = {
    sort_type: queryParams[sortingKeys.sort_type] || '',
    sort_field: queryParams[sortingKeys.sort_field] || '',
  };

  const id = pathParams.id;

  const theme = useTheme();

  const { gray1 } = theme.colors;

  const { tablet, narrowMonitor } = useMediaType();

  const { resetTable } = useTableFunctions();
  const history = useHistory();
  const isUserAccessManagementData = useSelector(selectUserAccessManagement);
  const isAccess = useSelector(selectIsAccess);
  const { view: isUserAccessManagementView } = isUserAccessManagementData;
  const languageStrings = useSelector(selectLanguageStrings);

  useEffect(() => {
    if (isAccess && !isUserAccessManagementView) {
      history.push(PATHS.ERROR_401);
    }
  }, [isAccess, isUserAccessManagementView]);

  useEffect(() => {
    return () => {
      resetTable();
    };
  }, []);

  // @TODO add possibility to use path params by the table

  return (
    <UniversalTable
      placeholderProps={{
        keyword,
        style: { height: 'max-content' },
        conditionalStyle: { overflow: 'hidden' },
        placeholderOptions: { countRows: 6, noScrollPrevention: true },
      }}
      keyword={keyword}
      offset={+offset}
      offsetKey={offsetKey}
      mainApiUrl={`${API_URL}/v2/devices/${id}/insight/events`}
      tablePath={View.USER_INSIGHTS_DETAILS}
      queryParams={queryParams}
      pathParams={pathParams}
      sortingData={sortingData}
      sortingKeys={sortingKeys}
      withHeaderInMobile={true}
      withHeaderGridTemplate={
        tablet ? '170px 2fr 24px' : narrowMonitor ? '170px 170px 1fr' : '0.6fr 0.5fr 0.8fr 1fr 4fr'
      }
      pageSize={10}
      inlineActionSet={
        tablet
          ? [
              {
                type: InlineActionType.DETAILS,
              },
            ]
          : []
      }
      columnStructureTriggers={[tablet, narrowMonitor]}
      columnStructure={[
        {
          fieldName: 'ts',
          placeholder: languageStrings.insightDetailsDeviceConfigurationEditInputsNamePlaceholder,
          label: languageStrings.problemsDate,
          defaultValue: '',
          excludedFromView: narrowMonitor,
          excludedFromAdd: narrowMonitor,
          excludedFromModalView: narrowMonitor,
          type: RowItemType.CUSTOM,
          component: (value: string, state) => (
            <Text
              type={TextType.TEXT_14_BLACK}
              color={gray1}
              margin={!narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '8px 12px' : '0'}
              weight={'400'}
            >
              {moment(value).format('DD.MM.YYYY') || 'N/A'}
            </Text>
          ),
        },
        {
          fieldName: 'ts',
          placeholder: languageStrings.insightDetailsDeviceConfigurationEditInputsNamePlaceholder,
          label: languageStrings.insightDetailsEventTime,
          defaultValue: '',
          excludedFromView: narrowMonitor,
          excludedFromAdd: narrowMonitor,
          excludedFromModalView: narrowMonitor,
          type: RowItemType.CUSTOM,
          component: (value: string, state) => (
            <Text
              type={TextType.TEXT_14_BLACK}
              color={gray1}
              margin={!narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '8px 12px' : '0'}
              weight={'400'}
            >
              {moment(value).format('HH:mm') || 'N/A'}
            </Text>
          ),
        },
        {
          fieldName: 'ts',
          placeholder: languageStrings.insightDetailsDeviceConfigurationEditInputsNamePlaceholder,
          label: languageStrings.insightDetalisEventDateAndTime,
          defaultValue: '',
          excludedFromView: !narrowMonitor,
          shownInHeader: narrowMonitor,
          excludedFromAdd: !narrowMonitor,
          excludedFromModalView: !narrowMonitor,
          type: RowItemType.CUSTOM,
          component: (value: string, state) => (
            <Text
              type={TextType.TEXT_14_BLACK}
              color={gray1}
              margin={narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '8px 12px' : '0'}
              weight={narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '400' : '500'}
            >
              {moment(value).format('DD.MM.YYYY, HH:mm') || 'N/A'}
            </Text>
          ),
        },
        {
          fieldName: 'type',
          placeholder: '',
          label: languageStrings.insightDetailsDeviceConfigurationEditInputsTypeTitle,
          defaultValue: '',
          shownInHeader: true,
          type: RowItemType.CUSTOM,
          component: (value: string, state) => (
            <Text
              type={TextType.TEXT_14_BLACK}
              color={gray1}
              margin={narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '8px 12px' : '0'}
              weight={narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '400' : '500'}
            >
              {value ?? 'N/A'}
            </Text>
          ),
        },
        {
          fieldName: 'source',
          placeholder: '',
          label: languageStrings.sourceTitle,
          defaultValue: '',
          excludedFromView: narrowMonitor,
          excludedFromAdd: narrowMonitor,
          excludedFromModalView: narrowMonitor,
          type: RowItemType.CUSTOM,
          component: (value: string, state) => (
            <Text
              type={TextType.TEXT_14_BLACK}
              color={gray1}
              margin={!narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '8px 12px' : '0'}
              weight={!narrowMonitor && state === TableCustomComponentState.VIEW_MODAL ? '400' : '500'}
            >
              {value ?? 'N/A'}
            </Text>
          ),
        },
        {
          fieldName: 'event',
          placeholder: languageStrings.manageClientsTableRowPlaceholdersEmail,
          label: languageStrings.insightDetailsEvent,
          defaultValue: 'user@example.com',
          excludedFromView: tablet,
          type: RowItemType.CUSTOM,
          component: (value: string, state?: TableCustomComponentState) => (
            <EventField value={value} state={state} isInDevices={isInDevices} />
          ),
        },
      ]}
    />
  );
};

export const EventTable = withTableContextProvider(TableControl);
